"use client";
import BlockWrapper from "block-system/components/BlockWrapper";
import { ChatbotBlock as ChatbotBlockType } from "../types";
import { useTheme } from "lib/theme";
import { usePageContext } from "lib/context/page-context";
import { BlockSpinner } from "block-system/components/BlockSpinner";
import { BlockId } from "block-system/types";
import { ZeroState } from "./ZeroState";
import { ChatbotDisabled, ChatbotDisabledEmbedded } from "./ChatbotDisabled";
import { Suspense } from "react";
import dynamic from "next/dynamic";
import { StandardBlockErrorState } from "block-system/components/BlockErrors/StandardBlockErrorState";

const ChatbotTailwind = dynamic(() => import("./ChatbotTailwind"), {
  ssr: false,
});

type ChatbotBlockProps = {
  block: ChatbotBlockType;
  blockId: BlockId;
};

export function ChatbotBlock(props: ChatbotBlockProps) {
  const chatbotId = props.block.config.chatbotId;

  const theme = useTheme();

  const { projectId, pageId, isEmbedded } = usePageContext();

  if (!projectId) throw new Error("Must provide projectId in app context");
  if (!pageId) throw new Error("Must provide pageId in app context");

  const blockConfig = props.block.config;

  return (
    <BlockWrapper
      blockId={props.blockId}
      block={props.block}
      maxWidth={
        blockConfig.style.width
          ? theme.app.width[blockConfig.style.width]
          : theme.app.width.wide
      }
      alignment={blockConfig.style.alignment}
    >
      {!chatbotId ? (
        <ZeroState title="No chatbot selected" />
      ) : props.block.errors?.length ? (
        <StandardBlockErrorState
          title="Chatbot unavailable"
          description="We encountered an error while loading the chatbot."
        />
      ) : (
        <Suspense fallback={<BlockSpinner />}>
          <ChatbotTailwind
            {...props}
            loadingSlot={BlockSpinner}
            disabledSlot={
              isEmbedded ? ChatbotDisabledEmbedded : ChatbotDisabled
            }
          />
        </Suspense>
      )}
    </BlockWrapper>
  );
}
